import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { StatCard } from '../components/ui/StatCard';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Table } from '../components/ui/Table';
import { InvoiceStatusBadge, SubscriptionStatusBadge } from '../components/domain/StatusBadge';
import { formatCurrency, formatDate, isOverdue, getCycleName } from '../utils/calculations';
import { Invoice, Subscription } from '../types';
import { DollarSignIcon, FileTextIcon, RepeatIcon, AlertTriangleIcon, PlusIcon } from 'lucide-react';
export function Dashboard() {
  const {
    filteredInvoices,
    filteredSubscriptions,
    getCustomerById,
    selectedCompany
  } = useData();
  const navigate = useNavigate();
  // Calculate stats
  const totalRevenue = filteredInvoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.total_amount, 0);
  const unpaidInvoices = filteredInvoices.filter(inv => inv.status === 'sent').length;
  const activeSubscriptions = filteredSubscriptions.filter(sub => sub.status === 'active').length;
  const overdueSubscriptions = filteredSubscriptions.filter(sub => sub.status === 'active' && isOverdue(sub.next_billing_date)).length;
  // Click handlers for stat cards
  const handleUnpaidClick = () => {
    navigate('/invoices', {
      state: {
        filterStatus: 'sent'
      }
    });
  };
  const handleActiveSubscriptionsClick = () => {
    navigate('/subscriptions', {
      state: {
        filterStatus: 'active'
      }
    });
  };
  const handleOverdueClick = () => {
    navigate('/subscriptions', {
      state: {
        filterStatus: 'overdue'
      }
    });
  };
  // Recent invoices (last 8)
  const recentInvoices = [...filteredInvoices].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 8);
  // Upcoming subscriptions (next 5 active)
  const upcomingSubscriptions = [...filteredSubscriptions].filter(sub => sub.status === 'active').sort((a, b) => new Date(a.next_billing_date).getTime() - new Date(b.next_billing_date).getTime()).slice(0, 5);
  const invoiceColumns = [{
    key: 'id',
    header: 'Invoice',
    render: (invoice: Invoice) => <Link to={`/invoices/${invoice.id}`} className="text-blue-600 hover:text-blue-800 font-medium" data-id="element-1002">
          {invoice.id.slice(0, 12)}...
        </Link>
  }, {
    key: 'customer',
    header: 'Customer',
    render: (invoice: Invoice) => {
      const customer = getCustomerById(invoice.customer_id);
      return customer?.name || 'Unknown';
    }
  }, {
    key: 'date',
    header: 'Date',
    render: (invoice: Invoice) => formatDate(invoice.date)
  }, {
    key: 'total',
    header: 'Total',
    render: (invoice: Invoice) => formatCurrency(invoice.total_amount)
  }, {
    key: 'status',
    header: 'Status',
    render: (invoice: Invoice) => <InvoiceStatusBadge status={invoice.status} data-id="element-1003" />
  }];
  const subscriptionColumns = [{
    key: 'customer',
    header: 'Customer',
    render: (sub: Subscription) => {
      const customer = getCustomerById(sub.customer_id);
      return customer?.name || 'Unknown';
    }
  }, {
    key: 'cycle',
    header: 'Cycle',
    render: (sub: Subscription) => getCycleName(sub.cycle_days)
  }, {
    key: 'nextBilling',
    header: 'Next Billing',
    render: (sub: Subscription) => {
      const overdue = isOverdue(sub.next_billing_date);
      return <span className={overdue ? 'text-red-600 font-medium' : ''} data-id="element-1004">
            {formatDate(sub.next_billing_date)}
            {overdue && ' (Overdue)'}
          </span>;
    }
  }, {
    key: 'status',
    header: 'Status',
    render: (sub: Subscription) => <SubscriptionStatusBadge status={sub.status} data-id="element-1005" />
  }];
  return <div className="space-y-8" data-id="element-1006">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-1007">
        <div data-id="element-1008">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1009">Dashboard</h1>
          <p className="text-gray-500 mt-1" data-id="element-1010">
            Welcome back! Here's what's happening with {selectedCompany?.name}.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-1011">
          <Link to="/invoices/new" data-id="element-1012">
            <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1014" />} data-id="element-1013">
              New Invoice
            </Button>
          </Link>
          <Link to="/subscriptions/new" data-id="element-1015">
            <Button variant="secondary" leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1017" />} data-id="element-1016">
              New Subscription
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1018">
        <StatCard title="Total Revenue" value={formatCurrency(totalRevenue)} icon={<DollarSignIcon className="w-6 h-6" data-id="element-1020" />} data-id="element-1019" />
        <StatCard title="Unpaid Invoices" value={unpaidInvoices} icon={<FileTextIcon className="w-6 h-6" data-id="element-1022" />} onClick={handleUnpaidClick} className="cursor-pointer hover:shadow-md transition-shadow" data-id="element-1021" />
        <StatCard title="Active Subscriptions" value={activeSubscriptions} icon={<RepeatIcon className="w-6 h-6" data-id="element-1024" />} onClick={handleActiveSubscriptionsClick} className="cursor-pointer hover:shadow-md transition-shadow" data-id="element-1023" />
        <StatCard title="Overdue Bills" value={overdueSubscriptions} icon={<AlertTriangleIcon className="w-6 h-6" data-id="element-1026" />} onClick={handleOverdueClick} className={`cursor-pointer hover:shadow-md transition-shadow ${overdueSubscriptions > 0 ? 'border-red-200 bg-red-50' : ''}`} data-id="element-1025" />
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-1027">
        {/* Recent Invoices */}
        <div className="lg:col-span-2" data-id="element-1028">
          <Card padding="none" data-id="element-1029">
            <div className="p-6 border-b border-gray-200" data-id="element-1030">
              <CardHeader title="Recent Invoices" subtitle="Your latest billing activity" action={<Link to="/invoices" data-id="element-1032">
                    <Button variant="ghost" size="sm" data-id="element-1033">
                      View All
                    </Button>
                  </Link>} data-id="element-1031" />
            </div>
            <Table columns={invoiceColumns} data={recentInvoices} keyExtractor={inv => inv.id} emptyMessage="No invoices yet" data-id="element-1034" />
          </Card>
        </div>

        {/* Upcoming Subscriptions */}
        <div data-id="element-1035">
          <Card padding="none" data-id="element-1036">
            <div className="p-6 border-b border-gray-200" data-id="element-1037">
              <CardHeader title="Upcoming Renewals" subtitle="Next billing dates" action={<Link to="/subscriptions" data-id="element-1039">
                    <Button variant="ghost" size="sm" data-id="element-1040">
                      View All
                    </Button>
                  </Link>} data-id="element-1038" />
            </div>
            <div className="divide-y divide-gray-200" data-id="element-1041">
              {upcomingSubscriptions.length === 0 ? <div className="p-6 text-center text-gray-500" data-id="element-1042">
                  No upcoming renewals
                </div> : upcomingSubscriptions.map(sub => {
              const customer = getCustomerById(sub.customer_id);
              const overdue = isOverdue(sub.next_billing_date);
              return <Link key={sub.id} to={`/subscriptions/${sub.id}`} className="block p-4 hover:bg-gray-50 transition-colors" data-id="element-1043">
                      <div className="flex items-center justify-between" data-id="element-1044">
                        <div data-id="element-1045">
                          <p className="font-medium text-gray-900" data-id="element-1046">
                            {customer?.name || 'Unknown'}
                          </p>
                          <p className="text-sm text-gray-500" data-id="element-1047">
                            {getCycleName(sub.cycle_days)}
                          </p>
                        </div>
                        <div className="text-right" data-id="element-1048">
                          <p className={`text-sm font-medium ${overdue ? 'text-red-600' : 'text-gray-900'}`} data-id="element-1049">
                            {formatDate(sub.next_billing_date)}
                          </p>
                          {overdue && <p className="text-xs text-red-600" data-id="element-1050">Overdue</p>}
                        </div>
                      </div>
                    </Link>;
            })}
            </div>
          </Card>
        </div>
      </div>
    </div>;
}